import * as React from 'react';
import { AlertTriangle, CloudOff, Timer } from 'lucide-react';

import { Badge } from '../primitives/badge';
import { cn } from '../lib/utils';
import { DegradedPanel, RetryWithCorrelation } from './degraded-state-panel';
import {
  DownstreamUnavailableState,
  ForbiddenState,
  OfflineBanner,
} from './degraded-state-banners';
import {
  blocksVerdict,
  classifyFailure,
  type ClassifiedFailure,
  type ClassifyOptions,
  type DegradedKind,
} from './degraded-state-core';

/**
 * The two entry points a view actually reaches for: the dispatcher that
 * routes one failure to the right surface, and the guard that degrades a
 * single rendered CONCLUSION to "unknown".
 *
 * Both exist so that "a 5xx rendered as a business verdict" is unreachable
 * by construction rather than by review.
 */

// ─── The dispatcher ───────────────────────────────────────────────────

export interface DegradedStateProps {
  /** The raw thrown value; classified internally. */
  error?: unknown;
  /** Pre-classified failure, when the caller already ran `classifyFailure`. */
  failure?: ClassifiedFailure;
  classifyOptions?: ClassifyOptions;
  /** Named upstream for the `downstream-unavailable` copy. */
  dependency?: string;
  /** Generic noun used by the forbidden surface. Never an identifier. */
  subject?: string;
  onRetry?: () => void;
  /**
   * The success content. Rendered ONLY when the classification is `ok` —
   * which is what makes "5xx rendered as a business verdict" unreachable
   * for any view that routes its failures through here.
   */
  children?: React.ReactNode;
  className?: string;
}

/**
 * Route one failure to the right degraded surface.
 *
 * A view that wraps its verdict in `<DegradedState error={query.error}>`
 * cannot render that verdict on a 500, a 503 or a 403, because the children
 * are not evaluated at all in those states.
 */
export function DegradedState({
  error,
  failure,
  classifyOptions,
  dependency,
  subject,
  onRetry,
  children,
  className,
}: DegradedStateProps) {
  const classified =
    failure ?? classifyFailure(error, { dependency, ...classifyOptions });

  switch (classified.kind) {
    case 'ok':
      return <>{children}</>;
    case 'forbidden':
      return (
        <ForbiddenState
          subject={subject}
          correlationId={classified.correlationId}
          className={className}
        />
      );
    case 'offline':
      return <OfflineBanner online={false} onRetry={onRetry} className={className} />;
    case 'downstream-unavailable':
      return (
        <DownstreamUnavailableState
          dependency={classified.dependency ?? dependency ?? 'A required service'}
          correlationId={classified.correlationId}
          onRetry={onRetry}
          className={className}
        />
      );
    case 'rate-limited':
      return (
        <DegradedPanel
          tone="warning"
          icon={<Timer className="h-4 w-4" />}
          title="Too many requests"
          testId="degraded-rate-limited"
          className={className}
          footer={
            <RetryWithCorrelation
              onRetry={onRetry}
              correlationId={classified.correlationId}
            />
          }
        >
          <p>
            This screen is being throttled.
            {classified.retryAfterSeconds !== undefined
              ? ` Try again in about ${classified.retryAfterSeconds}s.`
              : ' Try again shortly.'}{' '}
            Nothing below is a final answer.
          </p>
        </DegradedPanel>
      );
    case 'timeout':
      return (
        <DegradedPanel
          tone="danger"
          icon={<CloudOff className="h-4 w-4" />}
          title="The request timed out"
          testId="degraded-timeout"
          assertive
          className={className}
          footer={
            <RetryWithCorrelation
              onRetry={onRetry}
              correlationId={classified.correlationId}
            />
          }
        >
          <p>
            No result was received, so nothing below reflects the current state. A
            submitted change may or may not have been applied — check before
            resubmitting.
          </p>
        </DegradedPanel>
      );
    case 'not-found':
      return (
        <DegradedPanel
          tone="info"
          icon={<AlertTriangle className="h-4 w-4" />}
          title="Not found"
          testId="degraded-not-found"
          className={className}
          footer={
            <RetryWithCorrelation
              onRetry={onRetry}
              correlationId={classified.correlationId}
            />
          }
        >
          <p>{classified.message}</p>
        </DegradedPanel>
      );
    case 'validation':
      return (
        <DegradedPanel
          tone="danger"
          icon={<AlertTriangle className="h-4 w-4" />}
          title="Rejected"
          testId="degraded-validation"
          assertive
          className={className}
        >
          <p>{classified.message}</p>
        </DegradedPanel>
      );
    default:
      return (
        <DegradedPanel
          tone="danger"
          icon={<AlertTriangle className="h-4 w-4" />}
          title="This could not be loaded"
          testId="degraded-unavailable"
          assertive
          className={className}
          footer={
            <RetryWithCorrelation
              onRetry={onRetry}
              correlationId={classified.correlationId}
            />
          }
        >
          <p>
            {classified.message} Treat the area below as unknown rather than empty.
          </p>
        </DegradedPanel>
      );
  }
}

// ─── Verdict guard ────────────────────────────────────────────────────

export interface VerdictProps {
  /** The classification of the fetch backing this verdict. */
  kind: DegradedKind;
  /** Rendered only when the classification permits a conclusion. */
  children: React.ReactNode;
  /** Rendered instead when it does not. Defaults to an "unknown" chip. */
  unknown?: React.ReactNode;
  /** Short noun for what is unknown — "compliance status", "eligibility". */
  label?: string;
  className?: string;
}

/**
 * Wrap any rendered CONCLUSION — "compliant", "no findings", "eligible",
 * "no challenger proposed" — so it degrades to "unknown" rather than to a
 * false pass when the data behind it never arrived.
 *
 * This is the smallest possible fix for the recurring defect and is meant
 * to be applied at the exact expression that states the conclusion.
 */
export function Verdict({ kind, children, unknown, label, className }: VerdictProps) {
  if (!blocksVerdict(kind)) return <>{children}</>;
  return (
    <span className={cn('inline-flex items-center', className)} data-testid="verdict-unknown">
      {unknown ?? (
        <Badge variant="outline" className="gap-1">
          <AlertTriangle className="h-3 w-3" aria-hidden />
          {label ? `${label} unknown` : 'Unknown — could not be determined'}
        </Badge>
      )}
    </span>
  );
}
